import { defineComponent as _defineComponent } from 'vue'
import { type ObjectType } from '@/@types/graphql-types';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { ClickoutUrl, getClickoutUrlContextsArray, handleClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { computed } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutType } from '@/interfaces/snowplow/clickout-context';
import { inject } from 'vue';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { Maybe } from '@/helpers/types';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import ClickoutAnchor from '../nav/links/ClickoutAnchor.vue';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { useAppStore } from '@/stores/pinia/useAppStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'WatchNowButton',
  props: {
    titleObjectId: null,
    titleObjectType: null,
    title: { default: undefined },
    watchNowOffer: { default: () => ({}) },
    titleSeasonNumber: { default: undefined },
    lazy: { type: Boolean, default: true },
    size: { default: 'default' },
    rounded: { type: Boolean, default: true },
    color: { default: 'secondary' },
    inQuickActions: { type: Boolean, default: false },
    hideIcon: { type: Boolean, default: false },
    overwriteClickTracking: { type: Boolean, default: false },
    useClassicUrl: { type: Boolean, default: false },
    isSponsoredRecommendation: { type: Boolean, default: false },
    sponsoredRecommendationType: { default: 'REGULAR' },
    additionalContexts: { default: () => [] },
    isClickoutAnchor: { type: Boolean }
  },
  emits: ['watchNowClicked'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleObjectId: number, titleObjectType: ObjectType, title: TitleDetail, watchNowOffer: Maybe<WatchNowOfferFragment>, titleSeasonNumber: number, lazy: boolean, size: 'default' | 'large' | 'slim', rounded: boolean, color: 'secondary' | 'primary', inQuickActions: boolean, hideIcon: boolean, overwriteClickTracking: boolean, useClassicUrl: boolean, isSponsoredRecommendation: boolean, sponsoredRecommendationType: string, additionalContexts: SnowplowContext[], isClickoutAnchor?: boolean };





const { country } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());

const watchNowClickoutType = inject<MaybeRefOrGetter<ClickoutType> | undefined>('watchNowClickoutType', undefined);
const { clickoutType, placement } = getClickoutTrackingPlacement('watch_now');

const titleContext = computed(
	() =>
		new SnowplowTitleContextGraphql(
			props.titleObjectId,
			props.titleObjectType,
			props.titleSeasonNumber,
			undefined,
			undefined,
			undefined,
			props.title?.id
		)
);

const isModal = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(props.watchNowOffer?.package.technicalName || '')
);

const _isClickoutAnchor = computed(() => props.isClickoutAnchor && !isModal.value);

// Offer impression tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => [
		...(props.additionalContexts || []).filter(ctx => ctx.__name !== 'TitleContextGraphql'),
		titleContext.value,
	],
});

const clickoutUrl = computed(() => {
	const url = new ClickoutUrl(props.watchNowOffer)
		.setTitleSourceId(props.title?.id ?? null)
		.skipRedirect(!!props.useClassicUrl)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext)
		.set('uct_country', country.value)
		.set('uct_tryout', 1)
		.updateContext('ClickoutContextGraphql', 'clickoutType', toValue(watchNowClickoutType) ?? clickoutType)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id)
		.setCanary(xServeTrack.value);

	// Amazon streaming bounty redirects
	if ([9, 119].includes(props.watchNowOffer?.package?.packageId ?? 0)) {
		return url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const providerShortName = computed(() => props.watchNowOffer?.package?.shortName ?? undefined);

const { tc } = useI18n();
const watchNowLabel = computed(() =>
	props.sponsoredRecommendationType === 'CINEMATIC' && props.isSponsoredRecommendation
		? tc('WEBAPP_BUY_YOUR_TICKETS')
		: tc('WEBAPP_SEENLIST_WATCHNOW')
);

const playerByTechnicalName: Record<string, () => Promise<any>> = {
	plexplayer: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	justwatchtv: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	ottstudioplayer: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
};

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (props.watchNowOffer) onIntersection(clickoutUrl.value, isVisible);
}

const { openRentalModal } = useRentalModal();
function handleWatchNow(event: MouseEvent) {
	emits('watchNowClicked');

	if (!_isClickoutAnchor.value) event.preventDefault();

	if (!props.watchNowOffer) return;

	// JUSWATCH_TV
	if (isModal.value) {
		const isRentOffer = isJWTVRentalOffer(props.watchNowOffer);
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(clickoutUrl.value);
		if (isRentOffer) {
			openRentalModal({
				offer: props.watchNowOffer,
				title: props.title!,
				clickoutUrl: clickoutUrl.value,
				overrideRedirectUrl: true,
			});
		} else {
			ModalHelper.openModal(
				playerByTechnicalName[props.watchNowOffer.package.technicalName],
				{
					offer: props.watchNowOffer,
					title: props.title,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				},
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[]
			);
		}
	} else if (!_isClickoutAnchor.value) {
		handleClickoutUrl(clickoutUrl.value, false);
	}

	const contexts = [
		...(props.additionalContexts ?? []),
		titleContext.value,
		clickoutUrl.value.contexts.ClickoutContextGraphql,
	];

	if (!props.overwriteClickTracking) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'watchnow_clicked',
				property: props.inQuickActions + '',
			},
			contexts
		);
	}
}

return { __sfc: true,props, emits, country, xServeTrack, watchNowClickoutType, clickoutType, placement, titleContext, isModal, _isClickoutAnchor, onIntersection, clickoutUrl, providerShortName, tc, watchNowLabel, playerByTechnicalName, observeVisibilityOptions, onVisibilityChanged, openRentalModal, handleWatchNow, ProviderIcon, BasicButton, ClickoutAnchor }
}

})